import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSearch, faFilter, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const AuditUsers = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({});
    
    // Filter states
    const [filters, setFilters] = useState({
        action: '',
        actor_role: '',
        target_role: '',
        dateFrom: '',
        dateTo: '',
        search: ''
    });
    
    // Filter options
    const [filterOptions, setFilterOptions] = useState({
        actions: [],
        actor_roles: [],
        target_roles: []
    });
    
    // UI states
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        fetchFilterOptions();
        fetchLogs();
        // Initialize search input with current filter value
        setSearchInput(filters.search);
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [currentPage, filters]);

    // Debounced search effect
    useEffect(() => {
        if (searchInput !== filters.search) {
            setIsSearching(true);
            
            const timer = setTimeout(() => {
                setFilters(prev => ({ ...prev, search: searchInput }));
                setCurrentPage(1);
                setIsSearching(false);
            }, 500); // 500ms delay

            return () => {
                clearTimeout(timer);
                setIsSearching(false);
            };
        }
    }, [searchInput, filters.search]);

    // Helper function to translate actions to French
    const translateAction = (action) => {
        const translations = {
            'create': 'Création',
            'update': 'Modification', 
            'delete': 'Suppression',
            'restore': 'Restauration',
            'archive': 'Archivage',
            'login': 'Connexion',
            'logout': 'Déconnexion',
            'export': 'Export',
            'import': 'Import',
            'view': 'Consultation'
        };
        return translations[action] || action;
    };

    // Helper function to translate roles to French
    const translateRole = (role) => {
        const translations = {
            'administrateur_general_superieur': 'Business Officer',
            'administrateur_general': 'Administrateur Général',
            'resp_admin': 'Responsable Administratif',
            'resp_admin_superieur': 'Responsable Administratif Supérieur',
            'chef_terrain': 'Chef de Terrain',
            'chef_terrain_superieur': 'Head of operations',
            'charge_etude': 'Chargé d\'Étude',
            'charge_etude_superieur': 'Chargé d\'Étude Supérieur',
            'resp_cati': 'Responsable CATI',
            'resp_cati_superieur': 'Responsable CATI Supérieur',
            'resp_controle': 'Responsable Contrôle',
            'supervisor': 'Prestataire',
            'superviseur': 'Prestataire',
            'interviewer': 'Enquêteur',
            'enqueteur': 'Enquêteur',
            'controller': 'Contrôleur',
            'controleur': 'Contrôleur'
        };
        return translations[role] || role;
    };


    const fetchFilterOptions = async () => {
        try {
            const response = await api.get('/audit/filter-options');
            if (response.data.success) {
                // Use only the filtered options from API (role-based filtering)
                const actions = response.data.data.actions || [];
                const actor_roles = response.data.data.actor_roles || [];
                const target_roles = response.data.data.target_roles || [];
                
                setFilterOptions({ actions, actor_roles, target_roles });
            }
        } catch (error) {
            console.error('Error fetching filter options:', error);
            // Fallback to empty options if API fails
            setFilterOptions({
                actions: [],
                actor_roles: [],
                target_roles: []
            });
        }
    };

    const fetchLogs = async () => {
        try {
            setLoading(true);
            setError('');
            
            // Transform filter parameter names to match backend expectations
            const transformedFilters = {};
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== '') {
                    if (key === 'dateFrom') {
                        transformedFilters['date_from'] = value;
                    } else if (key === 'dateTo') {
                        transformedFilters['date_to'] = value;
                    } else {
                        transformedFilters[key] = value;
                    }
                }
            });

            const params = {
                page: currentPage,
                per_page: perPage,
                ...transformedFilters
            };

            const response = await api.get('/audit', { params });
            
            if (response.data.success) {
                setLogs(response.data.data.data);
                setPagination(response.data.data.pagination);
            } else {
                setError('Erreur lors du chargement des logs d\'audit');
            }
        } catch (error) {
            setError('Erreur lors du chargement des logs d\'audit');
            console.error('Error fetching audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1); // Reset to first page when filtering
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const clearFilters = () => {
        setFilters({
            action: '',
            actor_role: '',
            target_role: '',
            dateFrom: '',
            dateTo: '',
            search: ''
        });
        setSearchInput('');
        setIsSearching(false);
        setCurrentPage(1);
    };

    const handleExport = async () => {
        try {
            const response = await api.get('/audit/export', {
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting audit logs:', error);
        }
    };

    const handleFilteredExport = async () => {
        try {
            // Transform filter parameter names to match backend expectations
            const transformedFilters = {};
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== '') {
                    if (key === 'dateFrom') {
                        transformedFilters['date_from'] = value;
                    } else if (key === 'dateTo') {
                        transformedFilters['date_to'] = value;
                    } else {
                        transformedFilters[key] = value;
                    }
                }
            });
            
            const response = await api.get('/audit/export', {
                params: transformedFilters,
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `audit_logs_filtres_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting filtered audit logs:', error);
        }
    };

    const getActionColor = (action) => {
        const colors = {
            'Création': 'text-green-600',
            'Modification': 'text-blue-600',
            'Suppression': 'text-red-600',
            'Restauration': 'text-purple-600',
            'Archivage': 'text-orange-600',
            'Connexion': 'text-green-600',
            'Déconnexion': 'text-gray-600',
            'Export': 'text-indigo-600'
        };
        return colors[action] || 'text-gray-600';
    };

    const renderPagination = () => {
        const totalPages = pagination.last_page || 1;
        const totalCount = pagination.total || 0;

        if (totalPages <= 1) return null;

        return (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 gap-4">
                <div className="flex items-center text-xs sm:text-sm text-gray-700 text-center sm:text-left">
                    <span>
                        Affichage de {((currentPage - 1) * perPage) + 1} à {Math.min(currentPage * perPage, totalCount)} sur {totalCount} logs d'audit
                    </span>
                </div>
                
                <div className="flex items-center space-x-1 sm:space-x-2">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                    </button>
                    
                    <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }

                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`px-2 sm:px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        currentPage === pageNum
                                            ? 'bg-red-600 text-white shadow-md'
                                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                    </div>
                    
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Audit - Utilisateurs</h1>
                            <p className="text-gray-600 mt-1 text-sm sm:text-base">Journalisation et traçabilité des actions utilisateurs</p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-3 sm:space-y-0">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                    showFilters 
                                        ? 'text-white bg-red-600 hover:bg-red-700' 
                                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                <FontAwesomeIcon icon={faFilter} className="mr-1 sm:mr-2" />
                                Filtres
                            </button>
                            <button
                                onClick={handleExport}
                                className="flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                            >
                                <FontAwesomeIcon icon={faDownload} className="mr-1 sm:mr-2" />
                                <span className="hidden sm:inline">Exporter CSV</span>
                                <span className="sm:hidden">Export</span>
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    {showFilters && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                                {/* Action Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                                    <select
                                        value={filters.action}
                                        onChange={(e) => handleFilterChange('action', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    >
                                        <option value="">Toutes les actions</option>
                                        {filterOptions.actions.map(action => (
                                            <option key={action} value={action}>{translateAction(action)}</option>
                                        ))}
                                    </select>
                                </div>


                                {/* Actor Role Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Rôle Acteur</label>
                                    <select
                                        value={filters.actor_role}
                                        onChange={(e) => handleFilterChange('actor_role', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    >
                                        <option value="">Tous les rôles acteurs</option>
                                        {filterOptions.actor_roles.map(role => (
                                            <option key={role} value={role}>{translateRole(role)}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Target Role Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Rôle Cible</label>
                                    <select
                                        value={filters.target_role}
                                        onChange={(e) => handleFilterChange('target_role', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    >
                                        <option value="">Tous les rôles cibles</option>
                                        {filterOptions.target_roles.map(role => (
                                            <option key={role} value={role}>{translateRole(role)}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Date From */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                                    <input
                                        type="date"
                                        value={filters.dateFrom}
                                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                </div>

                                {/* Date To */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                                    <input
                                        type="date"
                                        value={filters.dateTo}
                                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                </div>

                                {/* Search */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Rechercher..."
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleFilterChange('search', searchInput);
                                                }
                                            }}
                                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                        />
                                        <FontAwesomeIcon 
                                            icon={faSearch} 
                                            className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-sm ${
                                                isSearching ? 'text-red-500' : 'text-gray-400'
                                            }`}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4">
                                <button
                                    onClick={handleFilteredExport}
                                    disabled={!Object.values(filters).some(filter => filter !== '')}
                                    className="flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                                >
                                    <FontAwesomeIcon icon={faDownload} className="mr-1 sm:mr-2" />
                                    <span className="hidden sm:inline">Exporter les filtres</span>
                                    <span className="sm:hidden">Export filtres</span>
                                </button>
                                <button
                                    onClick={clearFilters}
                                    className="flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                                >
                                    Effacer les filtres
                                </button>
                            </div>
                        </div>
                    )}
                </div>


                {/* Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="flex flex-col sm:flex-row items-center justify-center py-8 sm:py-12 px-4">
                            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-red-600"></div>
                            <span className="ml-0 sm:ml-3 mt-2 sm:mt-0 text-gray-600 text-sm sm:text-base text-center">Chargement des logs d'audit...</span>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center py-8 sm:py-12 px-4">
                            <div className="text-center">
                                <p className="text-red-600 mb-4 text-sm sm:text-base">{error}</p>
                                <button
                                    onClick={fetchLogs}
                                    className="px-3 sm:px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                                >
                                    Réessayer
                                </button>
                            </div>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="flex items-center justify-center py-8 sm:py-12 px-4">
                            <div className="text-center">
                                <p className="text-gray-500 mb-4 text-sm sm:text-base">Aucun log d'audit trouvé</p>
                                {Object.values(filters).some(filter => filter !== '') && (
                                    <div className="space-y-2">
                                        <p className="text-xs sm:text-sm text-gray-400">Essayez de modifier vos filtres ou de les effacer</p>
                                        <button
                                            onClick={clearFilters}
                                            className="px-3 sm:px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                                        >
                                            Effacer les filtres
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date/Heure
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Acteur
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Action
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Cible
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Rôle
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Description
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {logs.map((log) => (
                                            <tr key={log.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {log.when}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">{log.actor}</div>
                                                    <div className="text-sm text-gray-500">{translateRole(log.actor_role)}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`text-sm font-medium ${getActionColor(log.action)}`}>
                                                        {translateAction(log.action)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {log.target}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {translateRole(log.role)}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    {log.description}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Pagination */}
                            {renderPagination()}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuditUsers;
